/**
 * Read-only view of a saved capture - what "the document stored safely"
 * looks like after the fact. Reached from Home (document rows) and from
 * Actions (origin chips), closing the traceability loop.
 */
import { useParams } from 'react-router-dom';
import { useNavigation } from '@/hooks/useNavigationContext';
import { Box, Stack, Typography, Divider } from '@mui/material';

import { Card } from '@karehero/llama/components/atoms/Card/Card';
import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

import FlowScreen from './components/FlowScreen';
import { palette } from './components/palette';
import { useCaptureFlow } from './CaptureFlowContext';

const Bullets = ({ items }: { items: string[] }) => (
  <Stack spacing={1}>
    {items.map((item, i) => (
      <Stack
        sx={{ alignItems: 'flex-start' }}
        key={i}
        direction='row'
        spacing={1}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: palette.forest,
            mt: '7px',
            flexShrink: 0,
          }}
        />
        <Typography sx={{ fontSize: 14 }}>{item}</Typography>
      </Stack>
    ))}
  </Stack>
);

export const SavedDocumentView = () => {
  const navigate = useNavigation();
  const { id } = useParams();
  const { savedCaptures, completedActionKeys, deleteCapture } =
    useCaptureFlow();

  const capture = savedCaptures.find((c) => c.id === id);

  if (!capture) {
    return (
      <FlowScreen backTo='/'>
        <Stack spacing={2} sx={{ pt: 4, textAlign: 'center' }}>
          <Typography variant='h6'>This document is no longer here</Typography>
          <Typography sx={{ color: palette.mutedText, fontSize: 14 }}>
            It may have been deleted.
          </Typography>
        </Stack>
      </FlowScreen>
    );
  }

  const { doc } = capture;

  return (
    <FlowScreen>
      <Stack spacing={2.5} sx={{ pb: 2 }}>
        <PageHeader
          title={doc.classification}
          description={`Saved ${capture.savedAt.toLowerCase()} · stored in your KareHero documents.`}
        />

        <Card isFullWidth isPaddingSm>
          <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
            {doc.pageImages[0] ? (
              <Box
                component='img'
                src={doc.pageImages[0]}
                alt=''
                sx={{
                  width: 44,
                  height: 60,
                  objectFit: 'cover',
                  objectPosition: 'top',
                  borderRadius: 0.5,
                  border: `1px solid ${palette.divider}`,
                  backgroundColor: '#fff',
                }}
              />
            ) : (
              <Icon icon='file-lines' fontSize={22} />
            )}
            <Stack sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14 }}>{doc.fileName}</Typography>
              <Typography sx={{ color: palette.mutedText, fontSize: 12 }}>
                {doc.fileSizeKb}kb · {doc.pageCount} page
                {doc.pageCount === 1 ? '' : 's'} · via {capture.method}
              </Typography>
            </Stack>
          </Stack>
        </Card>

        {capture.markedWrong && (
          <Box sx={{ backgroundColor: '#fce9e5', borderRadius: 2, p: 1.5 }}>
            <Typography sx={{ fontSize: 13 }}>
              You flagged this summary as wrong. Your care manager can see that.
            </Typography>
          </Box>
        )}

        <Stack spacing={1}>
          <Typography variant='h6'>What it says</Typography>
          <Bullets items={doc.summary} />
        </Stack>

        <Stack spacing={1}>
          <Typography variant='h6'>What this means for you</Typography>
          <Bullets items={doc.meaning} />
        </Stack>

        <Divider sx={{ borderColor: palette.divider }} />

        <Stack spacing={1}>
          <Typography variant='h6'>Actions from this document</Typography>
          {capture.acceptedActions.length === 0 ? (
            <Typography sx={{ color: palette.mutedText, fontSize: 13 }}>
              You saved this document without adding actions.
            </Typography>
          ) : (
            capture.acceptedActions.map((action) => {
              const done = completedActionKeys.includes(
                `${capture.id}:${action.id}`,
              );
              return (
                <Typography key={action.id} sx={{ fontSize: 14 }}>
                  {done ? '✓' : '○'} {action.title}
                  {action.isTweaked && (
                    <Typography
                      component='span'
                      sx={{ fontSize: 12, color: palette.lavenderInk, ml: 0.5 }}
                    >
                      edited
                    </Typography>
                  )}
                </Typography>
              );
            })
          )}
        </Stack>

        <Button
          ariaLabel='Delete this document'
          variant='link'
          isFullWidth
          onPress={() => {
            deleteCapture(capture.id);
            navigate('/');
          }}
        >
          Delete this document
        </Button>
      </Stack>
    </FlowScreen>
  );
};

export default SavedDocumentView;
